<script lang="ts">
	import Mill from '$lib/logos/mill.svelte';
	import { headerVisible } from '$lib/stores/header';
	import ScrollDetector from '$lib/scrollDetector.svelte';
	import { afterNavigate, beforeNavigate } from '$app/navigation';
	import { browser } from '$app/environment';
	import type { HeaderVariant } from '../../types';
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '$lib/storyblok/editable';
	import Blok from '$lib/storyblok/blok.svelte';
	import { innerWidth } from 'svelte/reactivity/window';

	interface Props {
		content: ISbStoryData<{
			variant: HeaderVariant;
			button: ISbStoryData['content'][];
		}>['content'];
	}

	let { content }: Props = $props();

	let headerHeight = $state(999);

	function isSolid(scrollY = 0) {
		if (!browser) return false;

		const heroHeight = document.getElementById('hero')?.clientHeight || 0;

		return heroHeight === 0 || (heroHeight > 0 && scrollY >= heroHeight - headerHeight);
	}

	let solid = $state(isSolid());
	let transitionColor = $state(false);

	let buttonWidth = $state(999);

	let isMobile = $derived(!innerWidth.current || innerWidth.current < 768);
	let transformY = $derived(solid ? 0 : isMobile ? 0 : 16);
	let transformX = $derived(solid ? 0 : buttonWidth + (isMobile ? 12 : 16));

	function setSolid(scrollY: number) {
		solid = isSolid(scrollY);
	}

	function handleSolidState() {
		setSolid(window.scrollY);
	}

	function handleScrollStart() {
		transitionColor = true;
	}

	function handleScroll(y: number) {
		setSolid(y);
	}

	beforeNavigate(() => {
		transitionColor = false;
	});

	afterNavigate(() => {
		handleSolidState();
	});
</script>

<ScrollDetector onstart={handleScrollStart} onscroll={handleScroll} />

<header
	use:editable={content}
	id="site-header"
	class="fixed inset-x-0 z-50 transition-transform ease-out will-change-transform"
	style="transform: translate3d(0, var(--banner-height), 0)"
	class:duration-400={!$headerVisible}
	class:duration-600={$headerVisible}
>
	<nav
		class="relative z-20"
		class:transition-colors={transitionColor}
		class:duration-600={transitionColor}
		class:ease-out={transitionColor}
		class:bg-white={solid}
		bind:clientHeight={headerHeight}
	>
		<div class="px-6 md:px-8 xl:px-12">
			<div class="flex h-14 items-center justify-between py-[13px] lg:h-[100px] lg:py-6">
				<div
					class="flex transition-transform duration-1000 max-lg:mr-container-mobile xl:flex-1"
					style="transform: translate3d(0, {transformY}px, 0)"
				>
					<a href="/" title="Home">
						<Mill
							class="h-8 transition-colors duration-600 lg:h-11 {content.variant ===
								'transparent-white' && !solid
								? 'text-white'
								: 'text-green-3'}"
						/>
					</a>
				</div>

				{#if content.button.length > 0}
					<div
						class="flex flex-1 items-center justify-end gap-x-3 transition-transform duration-1000 lg:gap-x-8"
						class:delay-100={!solid}
						style="transform: translate3d({transformX}px,{transformY}px,0)"
					>
						<div
							class="transition-transform duration-1000"
							class:delay-100={solid}
							style="transform: translate3d({transformX}px,0,0)"
							bind:clientWidth={buttonWidth}
						>
							<Blok content={content.button[0]} />
						</div>
					</div>
				{/if}
			</div>
		</div>
	</nav>
</header>
